import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { MONTHS } from '../constants';

// Choose a scope and export CSV. Default scope is the prior month.
export default function ExportModal({ defaultMonthKey, onExport, onClose }) {
  const [scope, setScope] = useState('month'); // 'month' | 'all'
  const [month, setMonth] = useState(defaultMonthKey); // YYYY-MM
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const run = async () => {
    setBusy(true); setError('');
    try {
      await onExport(scope, scope === 'month' ? month : undefined);
      onClose();
    } catch (e) {
      setError(e?.message || 'Export failed.');
      setBusy(false);
    }
  };

  const [y, m] = month.split('-').map(Number);
  const inputCls = 'w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-white focus:border-blue-500 outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Export Safety Log (CSV)</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-700 text-slate-300"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="radio" name="scope" checked={scope === 'month'} onChange={() => setScope('month')} className="accent-blue-600" />
              Single month
            </label>
            {scope === 'month' && (
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} />
            )}
            {scope === 'month' && (
              <div className="text-xs text-slate-500">{MONTHS[(m || 1) - 1]} {y}</div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer pt-1">
              <input type="radio" name="scope" checked={scope === 'all'} onChange={() => setScope('all')} className="accent-blue-600" />
              All entries
            </label>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Elapsed days with no incidents export as "No Issues" rows. Timestamps are shown in
            Central Time (America/Chicago). Soft-deleted entries are excluded.
          </p>
          {error && <div className="text-sm text-red-400">{error}</div>}
        </div>
        <div className="border-t border-slate-700 px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700">Cancel</button>
          <button onClick={run} disabled={busy} className="text-sm px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 disabled:opacity-60">
            <Download className="w-4 h-4" /> {busy ? 'Exporting...' : 'Download CSV'}
          </button>
        </div>
      </div>
    </div>
  );
}
